import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators} from '@angular/forms';
import {addRemoveFormArray, toggleEditMode} from '../../../../shared/utils';
import {IHvAntrag} from '../../../models/hvantrag';
import {DatenBeratungsprotokollComponent} from '../../daten-beratungsprotokoll/daten-beratungsprotokoll.component';
import {HeimvorteilGebaeudeart, HeimvorteilProduktart} from '../../../../shared/enum';
import {TranslateService} from '@ngx-translate/core';
import {PopupService} from '../../../service/popup.service';
import {AntragWeitereFragenComponent} from '../antrag-weitere-fragen/antrag-weitere-fragen.component';
import {GebaeudetypDachComponent} from '../../calculatehv/gebaeudetyp-dach/gebaeudetyp-dach.component';
import {HaftpflichtHundeComponent} from '../../haftpflicht-hunde/haftpflicht-hunde.component';
import {HaftpflichtPersonenComponent} from '../../haftpflicht-personen/haftpflicht-personen.component';

@Component({
  selector: 'app-hv-antrag',
  templateUrl: './hv-antrag.component.html',
  styleUrls: ['./hv-antrag.component.scss']
})
export class HvAntragComponent implements OnInit, OnChanges {

  produktarten = HeimvorteilProduktart;
  gebaeudearten = HeimvorteilGebaeudeart;

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  weitereFragen: UntypedFormGroup;

  @Input()
  produktart: HeimvorteilProduktart;

  @Input()
  gebaeudeart: HeimvorteilGebaeudeart;

  @Input()
  haftpflichtAnzahlHunde: number;

  @Input()
  haftpflichtAnzahlWeiterePersonen: number;

  @Input()
  expertMode: boolean;

  @Output()
  kuendigungRisikenEvent = new EventEmitter<boolean>();

  @ViewChild(DatenBeratungsprotokollComponent) datenBeratungsprotokollComponent: DatenBeratungsprotokollComponent;
  @ViewChild(AntragWeitereFragenComponent) antragWeitereFragenComponent: AntragWeitereFragenComponent;
  @ViewChild(GebaeudetypDachComponent) gebaudeartDachComponent: GebaeudetypDachComponent;
  @ViewChildren(HaftpflichtHundeComponent) haftpflichtHundeComponents: QueryList<HaftpflichtHundeComponent>;
  @ViewChildren(HaftpflichtPersonenComponent) haftpflichtPersonenComponents: QueryList<HaftpflichtPersonenComponent>;

  constructor(public translateService: TranslateService, public popupService: PopupService, private fb: UntypedFormBuilder, private ref: ChangeDetectorRef) { }

  static createFormGroup(fb: UntypedFormBuilder) {
    return fb.group({
      vinkulierung: new UntypedFormControl(''),
      rohbaudeckung: new UntypedFormControl(''),
      glaeubigerVinkulierung: new UntypedFormControl('', Validators.required ),
      geschaeftszahlVinkulierung: new UntypedFormControl('', Validators.required),
      datenBeratungsprotokoll: DatenBeratungsprotokollComponent.createFormGroup(fb),
      gebaeudetypDach: GebaeudetypDachComponent.createFormGroup(fb),
      haftpflichtHundeEigenschaften: fb.array([]),
      haftpflichtWeiterePersonenEigenschaften: fb.array([]),
    });
  }

  ngOnInit(): void {
    this.onValueChanged();
    this.toggleEditMode();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.haftpflichtAnzahlHunde && changes.haftpflichtAnzahlHunde.currentValue !== changes.haftpflichtAnzahlHunde.previousValue) {
      if (changes.haftpflichtAnzahlHunde.currentValue > 0 ) {
        addRemoveFormArray(this.haftpflichtHundeEigenschaften, Number(changes.haftpflichtAnzahlHunde.currentValue),
          (): UntypedFormGroup => { return HaftpflichtHundeComponent.createFormGroup(this.fb); });
      } else {
        this.haftpflichtHundeEigenschaften.clear();
      }
      this.toggleEditMode();
    }

    if (changes.haftpflichtAnzahlWeiterePersonen && changes.haftpflichtAnzahlWeiterePersonen.currentValue !== changes.haftpflichtAnzahlWeiterePersonen.previousValue) {
      if(changes.haftpflichtAnzahlWeiterePersonen.currentValue > 0 ){
        addRemoveFormArray(this.haftpflichtWeiterePersonenEigenschaften, Number(changes.haftpflichtAnzahlWeiterePersonen.currentValue),
          (): UntypedFormGroup => { return HaftpflichtPersonenComponent.createFormGroup(this.fb); });
      }else{
        this.haftpflichtWeiterePersonenEigenschaften.clear();
      }

      this.toggleEditMode();
    }
  }

  toggleEditMode(): void {
    toggleEditMode(this.glaeubigerVinkulierung, this.showVinkulierungGroup);
    toggleEditMode(this.geschaeftszahlVinkulierung, this.showVinkulierungGroup);
  }

  onValueChanged(): void {
    this.vinkulierung.valueChanges.subscribe(() => {
      this.toggleEditMode();
    });
  }

  get datenBeratungsprotokoll() { return this.formGroup.get('datenBeratungsprotokoll'); }
  get vinkulierung() { return this.formGroup.get('vinkulierung'); }
  get rohbaudeckung() { return this.formGroup.get('rohbaudeckung'); }
  get gebaeudetypDach() { return this.formGroup.get('gebaeudetypDach'); }
  get glaeubigerVinkulierung() { return this.formGroup.get('glaeubigerVinkulierung'); }
  get geschaeftszahlVinkulierung() { return this.formGroup.get('geschaeftszahlVinkulierung'); }
  get haftpflichtHundeEigenschaften() { return this.formGroup.get('haftpflichtHundeEigenschaften') as UntypedFormArray; }
  get haftpflichtWeiterePersonenEigenschaften() { return this.formGroup.get('haftpflichtWeiterePersonenEigenschaften') as UntypedFormArray; }

  get showVinkulierungGroup() {
    return this.vinkulierung.value === 'ja';
  }

  get showHaftpflichtHundeEigenschaften() {
    return this.haftpflichtAnzahlHunde > 0;
  }

  get showHaftpflichtWeiterePersonenEigenschaften() {
    return this.haftpflichtAnzahlWeiterePersonen > 0;
  }

  get versicherungsArt() {
    return this.produktart === HeimvorteilProduktart.Haushalt ? 'Haushaltsversicherung' :
      this.produktart === HeimvorteilProduktart.Eigenheim ? 'Eigenheimversicherung' :
        this.produktart === HeimvorteilProduktart.Buendel ? 'Bündelversicherung' : null;
  }

  setKuendigungRisiken(kuendigungRisiken: boolean) {
    this.kuendigungRisikenEvent.emit(kuendigungRisiken);
  }

  setHvAntrag(hvAntrag: IHvAntrag) {
    this.datenBeratungsprotokollComponent.setOffertBase(hvAntrag);
    this.vinkulierung.setValue(hvAntrag.Vinkulierung === true ? 'ja' : hvAntrag.Vinkulierung === false ? 'nein' : null);
    this.rohbaudeckung.setValue(hvAntrag.Rohbaudeckung === true ? 'ja' : hvAntrag.Rohbaudeckung === false ? 'nein' : null);
    this.glaeubigerVinkulierung.setValue(hvAntrag.VinkulierungGlaeubiger);
    this.geschaeftszahlVinkulierung.setValue(hvAntrag.VinkulierungGeschaeftszahl);
    this.gebaudeartDachComponent.setDaten(hvAntrag.GebaeudetypDachung);
    this.antragWeitereFragenComponent.setAntrag(hvAntrag);

    addRemoveFormArray(this.haftpflichtHundeEigenschaften, Number(hvAntrag.Produkt.Erweiterungen.HaftpflichtHunde.length),
      (): UntypedFormGroup => { return HaftpflichtHundeComponent.createFormGroup(this.fb); });
    addRemoveFormArray(this.haftpflichtWeiterePersonenEigenschaften, Number(hvAntrag.Produkt.Erweiterungen.HaftpflichtWeiterePersonen.length),
      (): UntypedFormGroup => { return HaftpflichtPersonenComponent.createFormGroup(this.fb); });

    this.ref.detectChanges();

    for (let i = 0; i < hvAntrag.Produkt.Erweiterungen.HaftpflichtHunde.length; i++) {
      this.haftpflichtHundeComponents.toArray()[i].setHaftpflichtHund(hvAntrag.Produkt.Erweiterungen.HaftpflichtHunde[i]);
    }

    for (let i = 0; i < hvAntrag.Produkt.Erweiterungen.HaftpflichtWeiterePersonen.length; i++) {
      this.haftpflichtPersonenComponents.toArray()[i].setHaftpflichtPerson(hvAntrag.Produkt.Erweiterungen.HaftpflichtWeiterePersonen[i]);
    }
  }

  getHvAntrag(): IHvAntrag {
    return {
      Vinkulierung: this.vinkulierung.value ? this.vinkulierung.value === 'ja' ? true : false : null,
      Rohbaudeckung: this.rohbaudeckung.value ? this.rohbaudeckung.value === 'ja' ? true : false : null,
      VinkulierungGlaeubiger: this.glaeubigerVinkulierung.value,
      VinkulierungGeschaeftszahl: this.geschaeftszahlVinkulierung.value,
      GebaeudetypDachung: this.gebaudeartDachComponent.getDaten(),
      ...this.antragWeitereFragenComponent.getAntrag(),
      ...this.datenBeratungsprotokollComponent.getOffertBase()
    } as IHvAntrag;
  }

}
